// ============================================================================
// SEASON HUB
// ============================================================================
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { generateSchedule, getTeamSchedule, computeStandings, getPlayoffSeeds } from './scheduleGenerator';
import { simulateGame, quickSim, estimateAverages } from './gameSimulator';
import PLAYER_POSITIONS from './playerPositions';

const TEAMS_MAP = {
  ATL:{name:'Hawks',full:'Atlanta Hawks',nbaId:1610612737},BOS:{name:'Celtics',full:'Boston Celtics',nbaId:1610612738},BKN:{name:'Nets',full:'Brooklyn Nets',nbaId:1610612751},CHA:{name:'Hornets',full:'Charlotte Hornets',nbaId:1610612766},CHI:{name:'Bulls',full:'Chicago Bulls',nbaId:1610612741},CLE:{name:'Cavaliers',full:'Cleveland Cavaliers',nbaId:1610612739},DAL:{name:'Mavericks',full:'Dallas Mavericks',nbaId:1610612742},DEN:{name:'Nuggets',full:'Denver Nuggets',nbaId:1610612743},DET:{name:'Pistons',full:'Detroit Pistons',nbaId:1610612765},GSW:{name:'Warriors',full:'Golden State Warriors',nbaId:1610612744},HOU:{name:'Rockets',full:'Houston Rockets',nbaId:1610612745},IND:{name:'Pacers',full:'Indiana Pacers',nbaId:1610612754},LAC:{name:'Clippers',full:'LA Clippers',nbaId:1610612746},LAL:{name:'Lakers',full:'Los Angeles Lakers',nbaId:1610612747},MEM:{name:'Grizzlies',full:'Memphis Grizzlies',nbaId:1610612763},MIA:{name:'Heat',full:'Miami Heat',nbaId:1610612748},MIL:{name:'Bucks',full:'Milwaukee Bucks',nbaId:1610612749},MIN:{name:'Timberwolves',full:'Minnesota Timberwolves',nbaId:1610612750},NOP:{name:'Pelicans',full:'New Orleans Pelicans',nbaId:1610612740},NYK:{name:'Knicks',full:'New York Knicks',nbaId:1610612752},OKC:{name:'Thunder',full:'Oklahoma City Thunder',nbaId:1610612760},ORL:{name:'Magic',full:'Orlando Magic',nbaId:1610612753},PHI:{name:'76ers',full:'Philadelphia 76ers',nbaId:1610612755},PHX:{name:'Suns',full:'Phoenix Suns',nbaId:1610612756},POR:{name:'Trail Blazers',full:'Portland Trail Blazers',nbaId:1610612757},SAC:{name:'Kings',full:'Sacramento Kings',nbaId:1610612758},SAS:{name:'Spurs',full:'San Antonio Spurs',nbaId:1610612759},TOR:{name:'Raptors',full:'Toronto Raptors',nbaId:1610612761},UTA:{name:'Jazz',full:'Utah Jazz',nbaId:1610612762},WAS:{name:'Wizards',full:'Washington Wizards',nbaId:1610612764}
};

const teamLogo = (a) => `https://cdn.nba.com/logos/nba/${TEAMS_MAP[a]?.nbaId}/global/L/logo.svg`;
const teamName = (a) => TEAMS_MAP[a]?.name || a;
const teamFull = (a) => TEAMS_MAP[a]?.full || a;

function getRoster(sd, a) { const t = sd[a]; if (!t) return []; if (Array.isArray(t)) return t; if (Array.isArray(t.players)) return t.players; return []; }

function guessPosition(p, i) {
  // Use real position lookup first
  if (PLAYER_POSITIONS[p.name]) return PLAYER_POSITIONS[p.name];
  if (p.position) { const m={PG:'PG',SG:'SG',SF:'SF',PF:'PF',C:'C',G:'PG',F:'SF','G-F':'SG','F-G':'SF','F-C':'PF','C-F':'C'}; if(m[p.position]) return m[p.position]; }
  return ['PG','SG','SF','PF','C','PG','SG','SF','PF','C','SF','PF','C','SG','PG'][i%15]||'SF';
}

function buildPlayersForSim(sd, abbr, dc) {
  const chart = dc?.[abbr];
  const roster = getRoster(sd, abbr);
  const POSITIONS = ['PG', 'SG', 'SF', 'PF', 'C'];

  if (chart?.positions) {
    // Build from depth chart: use assigned positions and minutes EXACTLY
    const players = [];
    const added = new Set();
    for (const pos of POSITIONS) {
      for (const slot of (chart.positions[pos] || [])) {
        if (!slot?.name || slot.minutes === undefined || slot.minutes === null || added.has(slot.name)) continue;
        added.add(slot.name);
        // If minutes is 0, player is DNP — mark them but don't give stats
        if (slot.minutes === 0) {
          players.push({ name: slot.name, position: pos, isUserMinutes: true, isDNP: true, stats: { min: '0', pts: 0, reb: 0, ast: 0, stl: 0, blk: 0, turnover: 0, fg_pct: 0, fg3_pct: 0, ft_pct: 0, fga: 0, fg3a: 0, fta: 0 } });
          continue;
        }
        const stats = estimateAverages(slot.name, pos, 0, slot.minutes);
        players.push({ name: slot.name, position: pos, isUserMinutes: true, stats });
      }
    }
    // Only add non-depth-chart players if roster is very thin (< 8 playable)
    const playable = players.filter(p => !p.isDNP).length;
    if (playable < 8) {
      for (const p of roster) {
        if (p.salary2526 > 0 && !added.has(p.name) && !p.isDead && players.length < 15) {
          added.add(p.name);
          const pos = PLAYER_POSITIONS[p.name] || 'SF';
          const stats = estimateAverages(p.name, pos, 12, 3);
          players.push({ name: p.name, position: pos, stats });
        }
      }
    }
    return players;
  }

  // Fallback: salary-sort
  return roster.filter(p => p.salary2526 > 0).sort((a, b) => b.salary2526 - a.salary2526).slice(0, 13)
    .map((p, i) => ({ name: p.name, position: guessPosition(p, i), stats: estimateAverages(p.name, guessPosition(p, i), i) }));
}

function simGameDay(games, myTeam, sd, dc) {
  return games.map(g => {
    if (g.played) return g;
    const my = g.home===myTeam||g.away===myTeam;
    if (my) {
      return {...g,played:true,result:simulateGame(g.home,g.away,buildPlayersForSim(sd,g.home,dc),buildPlayersForSim(sd,g.away,dc))};
    }
    return {...g,played:true,result:quickSim(g.home,g.away,sd,dc)};
  });
}

function getMyConference(t) { return ['ATL','BOS','BKN','CHA','CHI','CLE','DET','IND','MIA','MIL','NYK','ORL','PHI','TOR','WAS'].includes(t)?'East':'West'; }

function checkAndAdvanceRound(p, conf, round) {
  const rd = p.rounds[conf][round]||[];
  if (rd.length===0||!rd.every(s=>s.winner)) return;
  const nr = round+1;
  if (nr>2) return;
  if (p.rounds[conf][nr]&&p.rounds[conf][nr].length>0) return;
  const w = rd.map(s=>s.winner);
  const m = [];
  for (let i=0;i<w.length;i+=2) { if(w[i]&&w[i+1]) m.push({home:w[i],away:w[i+1],homeWins:0,awayWins:0,games:[],winner:null}); }
  p.rounds[conf][nr] = m;
}

function getMyPlayoffResult(p, myTeam) {
  if (p.champion===myTeam) return {result:'Won Championship',round:'Finals',emoji:'champion'};
  if (p.finals?.loser===myTeam) return {result:`Lost NBA Finals ${Math.min(p.finals.homeWins,p.finals.awayWins)}-${Math.max(p.finals.homeWins,p.finals.awayWins)}`,round:'Finals',emoji:'finals'};
  const c = getMyConference(myTeam).toLowerCase();
  const rn = ['First Round','Conference Semis','Conference Finals'];
  for (let r=2;r>=0;r--) { for (const s of (p.rounds[c]?.[r]||[])) { if(s.loser===myTeam){const mw=s.winner===s.home?s.awayWins:s.homeWins;const ow=s.winner===s.home?s.homeWins:s.awayWins;return {result:`Lost ${rn[r]} ${mw}-${ow}`,round:rn[r],emoji:'eliminated'};} } }
  return {result:'Missed Playoffs',round:null,emoji:'lottery'};
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function SeasonHub({ myTeam, seasonState, salaryData, onUpdateSeason, onPhaseChange, onBeginFreeAgency, depthCharts }) {
  const [tab, setTab] = useState('schedule');
  const [expandedGame, setExpandedGame] = useState(null);
  const [expandedSeries, setExpandedSeries] = useState(null);
  const [simming, setSimming] = useState(false);
  const [showRecap, setShowRecap] = useState(false);

  const schedule = seasonState?.schedule || [];
  const playoffState = seasonState?.playoffs || null;
  const mySchedule = useMemo(() => getTeamSchedule(schedule, myTeam), [schedule, myTeam]);
  const myGamesPlayed = mySchedule.filter(g => g.played).length;
  const regularSeasonDone = myGamesPlayed >= 82 || mySchedule.filter(g => !g.played).length === 0;
  const standings = useMemo(() => computeStandings(schedule), [schedule]);
  const myRecord = standings.records[myTeam] || { wins:0, losses:0, pct:0, streak:0 };
  const currentGameDay = useMemo(() => { const u=schedule.find(g=>!g.played); return u?u.gameDay:999; }, [schedule]);

  // Phase sync
  const currentPhase = useMemo(() => {
    if (playoffState?.champion) return 'offseason';
    if (playoffState) return 'playoffs';
    if (myGamesPlayed >= 50) return 'postDeadline';
    return 'regular';
  }, [myGamesPlayed, playoffState]);

  useEffect(() => { if (onPhaseChange) onPhaseChange(currentPhase); }, [currentPhase, onPhaseChange]);

  const buildPlayers = useCallback((a) => {
    return buildPlayersForSim(salaryData, a, depthCharts);
  }, [salaryData, depthCharts]);

  // Regular season sim
  const simGames = useCallback((count) => {
    if (simming) return; setSimming(true);
    let u=[...schedule]; let gs=0; const md=Math.max(...schedule.map(g=>g.gameDay)); let d=currentGameDay;
    while(gs<count&&d<=md) {
      const dg=u.filter(g=>g.gameDay===d&&!g.played);
      if(dg.length>0){const sm=simGameDay(dg,myTeam,salaryData,depthCharts);const m=new Map(sm.map(g=>[g.id,g]));u=u.map(g=>m.has(g.id)?m.get(g.id):g);if(dg.some(g=>g.home===myTeam||g.away===myTeam))gs++;}
      d++;
    }
    onUpdateSeason({...seasonState,schedule:u}); setSimming(false);
  }, [schedule, currentGameDay, myTeam, salaryData, depthCharts, simming, onUpdateSeason, seasonState]);

  // Start playoffs
  const startPlayoffs = useCallback(() => {
    const seeds = getPlayoffSeeds(standings);
    const po = { seeds, playIn:{east:null,west:null}, rounds:{east:[[],[],[]],west:[[],[],[]]}, finals:null,champion:null,myResult:null };
    for (const c of ['east','west']) {
      const pi=seeds[c].playIn;
      if(pi.length>=4){
        const g78=quickSim(pi[0].abbr,pi[1].abbr,salaryData,depthCharts);
        const g910=quickSim(pi[2].abbr,pi[3].abbr,salaryData,depthCharts);
        const l78=g78.winner===pi[0].abbr?pi[1].abbr:pi[0].abbr;
        const gF=quickSim(l78,g910.winner,salaryData,depthCharts);
        po.playIn[c]={game78:g78,game910:g910,finalGame:gF,seed7:g78.winner,seed8:gF.winner};
      }
    }
    for (const c of ['east','west']) {
      const s=[...seeds[c].playoff]; const s7=po.playIn[c]?.seed7; const s8=po.playIn[c]?.seed8;
      const f=[...s]; if(s7)f.push({abbr:s7,seed:7}); if(s8)f.push({abbr:s8,seed:8}); f.sort((a,b)=>a.seed-b.seed);
      po.rounds[c][0]=[
        {home:f[0]?.abbr,away:f[7]?.abbr,homeSeed:1,awaySeed:8,homeWins:0,awayWins:0,games:[],winner:null},
        {home:f[1]?.abbr,away:f[6]?.abbr,homeSeed:2,awaySeed:7,homeWins:0,awayWins:0,games:[],winner:null},
        {home:f[2]?.abbr,away:f[5]?.abbr,homeSeed:3,awaySeed:6,homeWins:0,awayWins:0,games:[],winner:null},
        {home:f[3]?.abbr,away:f[4]?.abbr,homeSeed:4,awaySeed:5,homeWins:0,awayWins:0,games:[],winner:null},
      ];
    }
    onUpdateSeason({...seasonState,playoffs:po}); setTab('playoffs');
  }, [standings, salaryData, onUpdateSeason, seasonState]);

  // Sim one playoff game in a series
  const doSimGame = useCallback((series) => {
    if(series.winner||!series.home||!series.away) return false;
    const gn=(series.homeWins||0)+(series.awayWins||0);
    const ht=[0,1,4,6].includes(gn)?series.home:series.away;
    const at=ht===series.home?series.away:series.home;
    const my=series.home===myTeam||series.away===myTeam;
    const r=my?simulateGame(ht,at,buildPlayers(ht),buildPlayers(at)):quickSim(ht,at,salaryData,depthCharts);
    if(!series.games)series.games=[];
    series.games.push(r);
    if(r.winner===series.home)series.homeWins=(series.homeWins||0)+1; else series.awayWins=(series.awayWins||0)+1;
    if(series.homeWins>=4||series.awayWins>=4){series.winner=series.homeWins>=4?series.home:series.away;series.loser=series.winner===series.home?series.away:series.home;series.gamesPlayed=series.homeWins+series.awayWins;}
    return true;
  }, [myTeam, salaryData, depthCharts, buildPlayers]);

  const simOnePlayoffGame = useCallback(() => {
    if(!playoffState) return;
    const p=JSON.parse(JSON.stringify(playoffState));
    // Find first active series
    for(let r=0;r<3;r++){for(const c of ['east','west']){for(const s of (p.rounds[c][r]||[])){if(!s.winner&&s.home&&s.away){doSimGame(s);checkAndAdvanceRound(p,c,r);onUpdateSeason({...seasonState,playoffs:p});return;}}}}
    // Finals
    if(p.finals&&!p.finals.winner){doSimGame(p.finals);if(p.finals.winner){p.champion=p.finals.winner;p.myResult=getMyPlayoffResult(p,myTeam);}onUpdateSeason({...seasonState,playoffs:p,complete:!!p.champion});if(p.champion)setShowRecap(true);return;}
    // Create finals
    const ec=p.rounds.east[2]?.[0]?.winner;const wc=p.rounds.west[2]?.[0]?.winner;
    if(ec&&wc&&!p.finals){p.finals={home:wc,away:ec,homeWins:0,awayWins:0,games:[],winner:null};onUpdateSeason({...seasonState,playoffs:p});}
  }, [playoffState, myTeam, doSimGame, onUpdateSeason, seasonState]);

  const simPlayoffRound = useCallback(() => {
    if(!playoffState) return;
    const p=JSON.parse(JSON.stringify(playoffState));
    if(p.finals&&!p.finals.winner){doSimGame(p.finals);if(p.finals.winner){p.champion=p.finals.winner;p.myResult=getMyPlayoffResult(p,myTeam);}onUpdateSeason({...seasonState,playoffs:p,complete:!!p.champion});if(p.champion)setShowRecap(true);return;}
    let any=false;
    for(let r=0;r<3;r++){for(const c of ['east','west']){for(const s of (p.rounds[c][r]||[])){if(!s.winner&&s.home&&s.away){doSimGame(s);any=true;}}}if(any){for(const c of ['east','west'])checkAndAdvanceRound(p,c,r);break;}}
    const ec=p.rounds.east[2]?.[0]?.winner;const wc=p.rounds.west[2]?.[0]?.winner;
    if(ec&&wc&&!p.finals)p.finals={home:wc,away:ec,homeWins:0,awayWins:0,games:[],winner:null};
    onUpdateSeason({...seasonState,playoffs:p});
  }, [playoffState, myTeam, doSimGame, onUpdateSeason, seasonState]);

  const simAllPlayoffs = useCallback(() => {
    if(!playoffState) return;
    const p=JSON.parse(JSON.stringify(playoffState));
    const sf=(s)=>{while(!s.winner&&s.home&&s.away)doSimGame(s);};
    for(let r=0;r<3;r++){for(const c of ['east','west']){(p.rounds[c][r]||[]).forEach(sf);checkAndAdvanceRound(p,c,r);}}
    const ec=p.rounds.east[2]?.[0]?.winner;const wc=p.rounds.west[2]?.[0]?.winner;
    if(ec&&wc){if(!p.finals)p.finals={home:wc,away:ec,homeWins:0,awayWins:0,games:[],winner:null};sf(p.finals);if(p.finals.winner){p.champion=p.finals.winner;p.myResult=getMyPlayoffResult(p,myTeam);}}
    onUpdateSeason({...seasonState,playoffs:p,complete:!!p.champion});if(p.champion)setShowRecap(true);
  }, [playoffState, myTeam, doSimGame, onUpdateSeason, seasonState]);

  const playoffLabel = useMemo(() => {
    if(!playoffState) return '';
    if(playoffState.champion) return 'Season Over';
    if(playoffState.finals) return 'NBA Finals';
    for(let r=2;r>=0;r--){for(const c of ['east','west']){if((playoffState.rounds[c][r]||[]).some(s=>s.home&&!s.winner))return ['First Round','Conf. Semis','Conf. Finals'][r];}}
    return 'Playoffs';
  }, [playoffState]);

  // ========================================================================
  // RENDER
  // ========================================================================
  if (!myTeam) return <div className="season-hub"><div className="season-empty">Select a team in your save settings to begin.</div></div>;
  if (showRecap||(seasonState?.complete&&playoffState?.champion)) return <SeasonRecap myTeam={myTeam} standings={standings} playoffs={playoffState} myRecord={myRecord} onBack={()=>setShowRecap(false)} onBeginFreeAgency={() => onBeginFreeAgency && onBeginFreeAgency(standings)} />;

  return (
    <div className="season-hub">
      <div className="season-team-header">
        <img src={teamLogo(myTeam)} alt={myTeam} className="season-team-logo" />
        <div className="season-team-info"><div className="season-team-name">{teamFull(myTeam)}</div><div className="season-team-record">{myRecord.wins}-{myRecord.losses}</div></div>
        <div className="season-team-stats">
          <div className="season-stat-item"><span className="season-stat-val">{myGamesPlayed}</span><span className="season-stat-label">GP</span></div>
          <div className="season-stat-item"><span className="season-stat-val">{myRecord.pct?.toFixed(3)||'.000'}</span><span className="season-stat-label">PCT</span></div>
          <div className="season-stat-item"><span className="season-stat-val">{myRecord.streak>0?`W${myRecord.streak}`:myRecord.streak<0?`L${Math.abs(myRecord.streak)}`:'-'}</span><span className="season-stat-label">STRK</span></div>
        </div>
      </div>

      {!regularSeasonDone&&!playoffState&&(
        <div className="sim-controls">
          <div className="sim-controls-label">Simulate</div>
          <button className="sim-btn" onClick={()=>simGames(1)} disabled={simming}><span className="sim-btn-num">1</span><span className="sim-btn-text">Game</span></button>
          <button className="sim-btn" onClick={()=>simGames(5)} disabled={simming}><span className="sim-btn-num">5</span><span className="sim-btn-text">Games</span></button>
          <button className="sim-btn" onClick={()=>simGames(10)} disabled={simming}><span className="sim-btn-num">10</span><span className="sim-btn-text">Games</span></button>
          {myGamesPlayed<50&&<button className="sim-btn sim-btn-full" onClick={()=>simGames(Math.max(1,50-myGamesPlayed))} disabled={simming}>Sim to Deadline</button>}
          {myGamesPlayed>=50&&<button className="sim-btn sim-btn-full" onClick={()=>simGames(82-myGamesPlayed)} disabled={simming}>Sim to Playoffs</button>}
        </div>
      )}

      {regularSeasonDone&&!playoffState&&(
        <div className="sim-controls"><div className="season-complete-msg">Regular season complete — {myRecord.wins}-{myRecord.losses}</div><button className="sim-btn sim-btn-full" onClick={startPlayoffs}>Begin Playoffs →</button></div>
      )}

      {playoffState&&!playoffState.champion&&(
        <div className="sim-controls">
          <div className="sim-controls-label">{playoffLabel}</div>
          <button className="sim-btn" onClick={simOnePlayoffGame}><span className="sim-btn-num">1</span><span className="sim-btn-text">Game</span></button>
          <button className="sim-btn" onClick={simPlayoffRound}><span className="sim-btn-num">All</span><span className="sim-btn-text">Round</span></button>
          <button className="sim-btn sim-btn-full" onClick={simAllPlayoffs}>Sim All Playoffs</button>
        </div>
      )}

      {playoffState?.champion&&(
        <div className="sim-controls"><div className="season-complete-msg">{playoffState.champion===myTeam?'You won the championship!':`${teamFull(playoffState.champion)} won the title`}</div><button className="sim-btn sim-btn-full" onClick={()=>setShowRecap(true)}>View Season Recap →</button></div>
      )}

      <div className="season-tabs">
        <button className={`season-tab ${tab==='schedule'?'active':''}`} onClick={()=>setTab('schedule')}>Schedule</button>
        <button className={`season-tab ${tab==='standings'?'active':''}`} onClick={()=>setTab('standings')}>Standings</button>
        {playoffState&&<button className={`season-tab ${tab==='playoffs'?'active':''}`} onClick={()=>setTab('playoffs')}>Playoffs</button>}
      </div>

      {tab==='schedule'&&<ScheduleTab mySchedule={mySchedule} myTeam={myTeam} expandedGame={expandedGame} onToggleGame={(id)=>setExpandedGame(expandedGame===id?null:id)} />}
      {tab==='standings'&&<StandingsTab standings={standings} myTeam={myTeam} />}
      {tab==='playoffs'&&playoffState&&<PlayoffBracket playoffs={playoffState} myTeam={myTeam} expandedSeries={expandedSeries} onToggleSeries={(k)=>setExpandedSeries(expandedSeries===k?null:k)} />}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================
function ScheduleTab({mySchedule,myTeam,expandedGame,onToggleGame}) {
  return (<div className="schedule-list">{mySchedule.map((g,i)=>{
    const won=g.played&&g.result?.winner===myTeam;const lost=g.played&&g.result?.winner&&g.result.winner!==myTeam;const isExp=expandedGame===g.id;const hasBox=g.result?.homeBox;
    return (<div key={g.id} className={`schedule-game ${g.played?'played':'upcoming'} ${won?'won':''} ${lost?'lost':''}`}>
      <button className="schedule-game-row" onClick={()=>g.played&&onToggleGame(g.id)}>
        <span className="schedule-game-num">G{i+1}</span><span className={`schedule-game-wl ${won?'w':lost?'l':''}`}>{won?'W':lost?'L':'-'}</span>
        <span className="schedule-game-loc">{g.isHome?'vs':'@'}</span><img src={teamLogo(g.opponent)} alt="" className="schedule-opp-logo" /><span className="schedule-opp-name">{teamName(g.opponent)}</span>
        {g.played&&g.result&&<span className="schedule-score">{g.isHome?`${g.result.homeScore}-${g.result.awayScore}`:`${g.result.awayScore}-${g.result.homeScore}`}</span>}
        {!g.played&&<span className="schedule-upcoming-label">Upcoming</span>}
        {hasBox&&<span className="schedule-expand-icon">{isExp?'▲':'▼'}</span>}
      </button>
      {isExp&&hasBox&&<BoxScoreView result={g.result} myTeam={myTeam} />}
    </div>);
  })}</div>);
}

function BoxScoreView({result,myTeam}) {
  const {homeBox,awayBox,homeTeam,awayTeam,homeScore,awayScore,homeQuarters,awayQuarters}=result;
  return (<div className="box-score">
    <div className="box-quarters"><table className="box-quarter-table"><thead><tr><th></th><th>Q1</th><th>Q2</th><th>Q3</th><th>Q4</th><th>T</th></tr></thead><tbody>
      <tr className={homeTeam===myTeam?'my-team':''}><td className="box-q-team"><img src={teamLogo(homeTeam)} alt="" className="box-q-logo"/>{homeTeam}</td>{homeQuarters?.map((q,i)=><td key={i}>{q}</td>)}<td className="box-q-total">{homeScore}</td></tr>
      <tr className={awayTeam===myTeam?'my-team':''}><td className="box-q-team"><img src={teamLogo(awayTeam)} alt="" className="box-q-logo"/>{awayTeam}</td>{awayQuarters?.map((q,i)=><td key={i}>{q}</td>)}<td className="box-q-total">{awayScore}</td></tr>
    </tbody></table></div>
    {[{l:homeTeam,b:homeBox},{l:awayTeam,b:awayBox}].map(({l,b})=>(<div key={l} className="box-team-section"><div className="box-team-label"><img src={teamLogo(l)} alt="" className="box-team-label-logo"/>{teamFull(l)}</div><div className="box-table-wrap"><table className="box-table"><thead><tr><th className="box-player-col">Player</th><th>MIN</th><th>PTS</th><th>REB</th><th>AST</th><th>FG</th><th>3P</th><th>FT</th><th>STL</th><th>BLK</th><th>TO</th><th>+/-</th></tr></thead><tbody>
      {b?.map((p,i)=>(<tr key={i} className={p.minutes>20?'starter':''}><td className="box-player-col"><span className="box-player-name">{p.name}</span><span className="box-player-pos">{p.position}</span></td><td>{p.minutes}</td><td className="box-pts">{p.pts}</td><td>{p.reb}</td><td>{p.ast}</td><td>{p.fgm}-{p.fga}</td><td>{p.threeMade}-{p.threeAtt}</td><td>{p.ftm}-{p.fta}</td><td>{p.stl}</td><td>{p.blk}</td><td>{p.tov}</td><td className={p.plusMinus>0?'positive':p.plusMinus<0?'negative':''}>{p.plusMinus>0?'+':''}{p.plusMinus}</td></tr>))}
    </tbody></table></div></div>))}
  </div>);
}

function StandingsTab({standings,myTeam}) {
  const renderConf=(label,teams)=>(<div className="standings-conf"><div className="standings-conf-label">{label}</div><table className="standings-table"><thead><tr><th className="standings-rank">#</th><th className="standings-team-col">Team</th><th>W</th><th>L</th><th>PCT</th><th className="standings-hide-mobile">CONF</th><th className="standings-hide-mobile">STRK</th><th className="standings-hide-mobile">L10</th></tr></thead><tbody>
    {teams.map((t,i)=>{const l10=t.last10||[];return(<tr key={t.abbr} className={`${t.abbr===myTeam?'my-team-row':''} ${i<6?'playoff-team':''} ${i>=6&&i<10?'playin-team':''}`}><td className="standings-rank">{i+1}</td><td className="standings-team-col"><img src={teamLogo(t.abbr)} alt="" className="standings-logo"/><span>{t.abbr}</span></td><td>{t.wins}</td><td>{t.losses}</td><td>{t.pct?.toFixed(3)||'.000'}</td><td className="standings-hide-mobile">{t.confWins}-{t.confLosses}</td><td className="standings-hide-mobile">{t.streak>0?`W${t.streak}`:t.streak<0?`L${Math.abs(t.streak)}`:'-'}</td><td className="standings-hide-mobile">{l10.filter(x=>x==='W').length}-{l10.filter(x=>x==='L').length}</td></tr>);})}
  </tbody></table></div>);
  return (<div className="standings-wrap">{renderConf('Eastern Conference',standings.east)}{renderConf('Western Conference',standings.west)}</div>);
}

function PlayoffBracket({playoffs,myTeam,expandedSeries,onToggleSeries}) {
  const [expandedPGame, setExpandedPGame] = React.useState(null);
  if(!playoffs) return null;
  const renderSeries=(s,flip,key)=>{
    if(!s?.home) return <div className="bracket-series empty">TBD</div>;
    const top=flip?s.away:s.home;const bot=flip?s.home:s.away;
    const tw=flip?s.awayWins:s.homeWins;const bw=flip?s.homeWins:s.awayWins;
    const isExp=expandedSeries===key;const hasG=s.games&&s.games.length>0;const my=s.home===myTeam||s.away===myTeam;
    return (<div className={`bracket-series ${s.winner?'decided':'live'}`}>
      <button className="bracket-series-btn" onClick={()=>hasG&&onToggleSeries(key)}>
        <div className={`bracket-team ${s.winner===top?'winner':''} ${top===myTeam?'my-team':''}`}><img src={teamLogo(top)} alt="" className="bracket-logo"/><span className="bracket-name">{top}</span><span className="bracket-wins">{tw||0}</span></div>
        <div className={`bracket-team ${s.winner===bot?'winner':''} ${bot===myTeam?'my-team':''}`}><img src={teamLogo(bot)} alt="" className="bracket-logo"/><span className="bracket-name">{bot}</span><span className="bracket-wins">{bw||0}</span></div>
        {hasG&&<span className="bracket-expand-hint">{isExp?'▲':'▼'}</span>}
      </button>
      {isExp&&hasG&&(<div className="bracket-series-detail">
        {s.games.map((g,gi)=>{
          const mw=my&&g.winner===myTeam;const ml=my&&g.winner!==myTeam;
          const hasBox=g.homeBox&&g.homeBox.length>0;
          const gameKey=`${key}-g${gi}`;
          const isGameExp=expandedPGame===gameKey;
          return(<div key={gi}>
            <div className={`bracket-game-row ${mw?'won':''} ${ml?'lost':''}`}>
              <span className="bracket-game-num">G{gi+1}</span>
              <span className="bracket-game-score">{g.homeTeam} {g.homeScore} - {g.awayScore} {g.awayTeam}</span>
              {hasBox&&<button className="bracket-game-box-btn" onClick={(e)=>{e.stopPropagation();setExpandedPGame(isGameExp?null:gameKey);}}>
                {isGameExp?'Hide':'Box Score'}
              </button>}
            </div>
            {isGameExp&&hasBox&&<BoxScoreView result={g} myTeam={myTeam} />}
          </div>);
        })}
      </div>)}
    </div>);
  };
  const wr=playoffs.rounds?.west||[[],[],[]];const er=playoffs.rounds?.east||[[],[],[]];
  return (<div className="playoff-bracket-v2"><div className="bracket-title">Playoffs</div><div className="bracket-layout">
    <div className="bracket-side west"><div className="bracket-conf-tag">Western Conference</div>
      <div className="bracket-col r1"><div className="bracket-round-label">First Round</div>{(wr[0]||[]).map((s,i)=><div key={i}>{renderSeries(s,false,`w0-${i}`)}</div>)}</div>
      <div className="bracket-col r2"><div className="bracket-round-label">Conf. Semis</div>{(wr[1]||[]).map((s,i)=><div key={i}>{renderSeries(s,false,`w1-${i}`)}</div>)}</div>
      <div className="bracket-col r3"><div className="bracket-round-label">Conf. Finals</div>{(wr[2]||[]).map((s,i)=><div key={i}>{renderSeries(s,false,`w2-${i}`)}</div>)}</div>
    </div>
    <div className="bracket-center"><div className="bracket-round-label">NBA Finals</div>
      {playoffs.finals?renderSeries(playoffs.finals,false,'finals'):<div className="bracket-series empty">TBD</div>}
      {playoffs.champion&&<div className="bracket-champion-badge"><img src={teamLogo(playoffs.champion)} alt="" className="bracket-champ-logo"/><div className="bracket-champ-text">{teamFull(playoffs.champion)}</div><div className="bracket-champ-sub">NBA Champions</div></div>}
    </div>
    <div className="bracket-side east"><div className="bracket-conf-tag">Eastern Conference</div>
      <div className="bracket-col r3"><div className="bracket-round-label">Conf. Finals</div>{(er[2]||[]).map((s,i)=><div key={i}>{renderSeries(s,true,`e2-${i}`)}</div>)}</div>
      <div className="bracket-col r2"><div className="bracket-round-label">Conf. Semis</div>{(er[1]||[]).map((s,i)=><div key={i}>{renderSeries(s,true,`e1-${i}`)}</div>)}</div>
      <div className="bracket-col r1"><div className="bracket-round-label">First Round</div>{(er[0]||[]).map((s,i)=><div key={i}>{renderSeries(s,true,`e0-${i}`)}</div>)}</div>
    </div>
  </div></div>);
}

function SeasonRecap({myTeam,standings,playoffs,myRecord,onBack,onBeginFreeAgency}) {
  const result=playoffs?.myResult||getMyPlayoffResult(playoffs,myTeam);
  const conf=getMyConference(myTeam);const cs=conf==='East'?standings.east:standings.west;
  const seed=cs.findIndex(t=>t.abbr===myTeam)+1;const isC=playoffs?.champion===myTeam;
  const champion=playoffs?.champion;
  const finals=playoffs?.finals;
  const finalsScore = finals ? `${Math.max(finals.homeWins,finals.awayWins)}-${Math.min(finals.homeWins,finals.awayWins)}` : '';
  const finalsWinner = finals?.winner;
  const finalsLoser = finalsWinner === finals?.home ? finals?.away : finals?.home;

  return (<div className="season-recap">
    {/* League Champion Section */}
    {champion && finals && (
      <div className="recap-champion-section">
        <div className="recap-mono-label" style={{textAlign:'center',marginBottom:8}}>2025-26 NBA Champions</div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:16,marginBottom:12}}>
          <img src={teamLogo(champion)} alt="" style={{width:64,height:64}} />
          <div>
            <div style={{fontFamily:'var(--font-display)',fontSize:26,fontWeight:700}}>{teamFull(champion)}</div>
            <div style={{fontFamily:'var(--font-mono)',fontSize:12,color:'var(--text-muted)'}}>
              defeated {teamFull(finalsLoser)} {finalsScore}
            </div>
          </div>
        </div>
        {finals.games && finals.games.length > 0 && (
          <div className="recap-finals-games">
            <div className="recap-mono-label" style={{marginBottom:6}}>NBA Finals Results</div>
            {finals.games.map((g,i) => (
              <div key={i} className="recap-finals-game-row">
                <span style={{fontFamily:'var(--font-mono)',fontSize:9,color:'var(--text-dim)',minWidth:50}}>Game {i+1}</span>
                <span style={{fontFamily:'var(--font-display)',fontSize:12}}>
                  {g.winner === finals.home ? teamFull(finals.home) : teamFull(finals.away)}
                </span>
                <span style={{fontFamily:'var(--font-mono)',fontSize:11,color:'var(--text-muted)'}}>
                  {g.homeScore}-{g.awayScore}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}

    {/* Your Team Section */}
    <div className="recap-header" style={{marginTop: champion ? 24 : 0}}>
      <div className="recap-mono-label">Your Season</div>
      <img src={teamLogo(myTeam)} alt="" className="recap-logo"/>
      <div className="recap-team-name">{teamFull(myTeam)}</div>
      <div className="recap-record">{myRecord.wins}-{myRecord.losses}</div>
      <div className="recap-seed">#{seed} Seed — {conf}ern Conference</div>
    </div>
    <div className={`recap-result ${result.emoji}`}>
      <div className="recap-result-text">{result.result}</div>
      {isC&&<div className="recap-champ-text">You did it. NBA Champions.</div>}
    </div>
    <div className="recap-stats">
      <div className="recap-stat"><div className="recap-stat-val">{myRecord.wins}</div><div className="recap-stat-label">Wins</div></div>
      <div className="recap-stat"><div className="recap-stat-val">{myRecord.losses}</div><div className="recap-stat-label">Losses</div></div>
      <div className="recap-stat"><div className="recap-stat-val">{myRecord.pct?.toFixed(3)}</div><div className="recap-stat-label">Win %</div></div>
      <div className="recap-stat"><div className="recap-stat-val">#{seed}</div><div className="recap-stat-label">Seed</div></div>
    </div>
    {isC&&<div className="recap-banner"><div className="recap-banner-year">2025-26</div><div className="recap-banner-title">NBA Champions</div></div>}
    <div style={{display:'flex',gap:8,justifyContent:'center',marginTop:16}}>
      <button className="recap-back-btn" onClick={onBack}>&larr; Back to Season</button>
      {onBeginFreeAgency && <button className="recap-fa-btn" onClick={onBeginFreeAgency}>Begin Draft &rarr;</button>}
    </div>
  </div>);
}
